import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { clientesApi } from "src/services/clientesApi";
import { dbx } from "src/utils/urlBack_End";

const ROLES_SIN_RESTRICCION = ["ADMIN", "SUPER_ADMIN"];
const DIAS_VENTANA_EDICION = 3;

// Replica el LPAD de la migración: hay horas guardadas como "9:05:00", que
// ordenadas como texto quedarían después de "10:30:00".
const claveCronologica = (fila) =>
  `${fila?.fecha ?? ""} ${String(fila?.hora ?? "").padStart(8, "0")}`;

// Con varias bitácoras el mismo día, created_at desempata; las migradas que no
// lo tengan caen al orden por fecha.
const instanteCreacion = (portafolio) => {
  const fecha = new Date(portafolio?.created_at || portafolio?.fecha);
  return Number.isNaN(fecha.getTime()) ? 0 : fecha.getTime();
};

// Días calendario desde la creación, igual que el backend. Recibe `fecha`
// (texto YYYY/MM/DD) y no `created_at`: ese llega serializado como ISO en UTC y
// el navegador lo reproyecta, corriendo el día y escondiendo el botón una
// jornada antes. Acá solo se pinta; la decisión la toma el servidor.
const diasCalendarioDesde = (valor) => {
  if (!valor) return null;
  const creado = new Date(valor);
  if (Number.isNaN(creado.getTime())) return null;
  const hoy = new Date();
  const inicioCreado = new Date(
    creado.getFullYear(),
    creado.getMonth(),
    creado.getDate(),
  );
  const inicioHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.round((inicioHoy - inicioCreado) / 86400000);
};

export const usePortafolio = (clienteRut) => {
  const {
    empleado: user,
    sucursal,
    rol,
  } = useSelector((state) => state.auth.user);
  const [deletingFile, setDeletingFile] = useState(false);
  const [deletingPortafolio, setDeletingPortafolio] = useState(false);

  // RTK Query para obtener portafolios
  const {
    data: portafoliosData,
    isLoading,
    error,
    refetch,
  } = clientesApi.useObtenerPortafoliosClienteQuery(
    { rut: clienteRut },
    { skip: !clienteRut },
  );

  // Mutation para eliminar archivos
  const [eliminarArchivoMutation] =
    clientesApi.useEliminarArchivoPortafolioMutation();

  // Mutation para eliminar bitácora (soft delete)
  const [eliminarPortafolioMutation] =
    clientesApi.useEliminarPortafolioClienteMutation();

  // Crear carpeta en Dropbox si no existe
  const createDropboxFolder = useCallback(async () => {
    if (!clienteRut) return;

    try {
      await dbx.filesListFolder({ path: `/Clientes/${clienteRut}/Portafolio` });
    } catch {
      try {
        await dbx.filesCreateFolderV2({
          path: `/Clientes/${clienteRut}/Portafolio`,
        });
      } catch (error) {
        console.error("Error creando carpeta en Dropbox:", error);
      }
    }
  }, [clienteRut]);

  // Eliminar archivo
  const deleteFile = useCallback(
    async (archivo) => {
      setDeletingFile(true);
      if (!archivo || !clienteRut) {
        return { success: false, error: "Datos incompletos" };
      }

      try {
        // Eliminar de Dropbox
        await dbx.filesDeleteV2({
          path: `/Clientes/${clienteRut}/Portafolio/${archivo.nombreArchivo}`,
        });

        // Eliminar de BD usando RTK Query
        await eliminarArchivoMutation({
          id: archivo.id,
          refPortafolioCliente: archivo.refPortafolioCliente,
          refSucursal: sucursal?.refSucursal,
        }).unwrap();

        return { success: true };
      } catch (error) {
        console.error("Error eliminando archivo:", error);
        return {
          success: false,
          error: error.data?.message || "Error al eliminar el archivo",
        };
      } finally {
        setDeletingFile(false);
      }
    },
    [clienteRut, sucursal, eliminarArchivoMutation],
  );

  // Procesar datos para el render
  const processedPortafolios = useMemo(() => {
    if (!portafoliosData || !Array.isArray(portafoliosData)) {
      return [];
    }

    const esAdmin = ROLES_SIN_RESTRICCION.includes(rol);

    return portafoliosData
      .map((portafolio) => {
        const historialPortafolios = portafolio.historialPortafolios || [];
        const archivos = portafolio.archivos || [];

        const historialOrdenado = [...historialPortafolios]
          .filter((element) => element.refPortafolioCliente === portafolio.id)
          .sort((a, b) =>
            claveCronologica(a).localeCompare(claveCronologica(b)),
          );

        // El nombre del autor sale del historial, no de created_by: hay cuentas
        // reasignadas cuyo titular actual no es quien escribió la bitácora.
        const creacion = historialOrdenado[0];
        const ultimaModificacion =
          historialOrdenado[historialOrdenado.length - 1];

        const diasDesdeCreacion = diasCalendarioDesde(portafolio.fecha);
        const esAutor =
          Boolean(portafolio.created_by) && portafolio.created_by === user?.rut;
        const dentroDeVentana =
          diasDesdeCreacion !== null &&
          diasDesdeCreacion <= DIAS_VENTANA_EDICION - 1;

        // Filtrar archivos del portafolio
        const archivosPortafolio = archivos.filter(
          (element) => element.refPortafolioCliente === portafolio.id,
        );

        return {
          ...portafolio,
          creacion,
          creador: creacion?.empleado,
          ultimaModificacion,
          mostrarUltimaEdicion:
            Boolean(ultimaModificacion) &&
            ultimaModificacion?.id !== creacion?.id,
          puedeModificar: esAdmin || (esAutor && dentroDeVentana),
          archivosPortafolio,
        };
      })
      .sort((a, b) => instanteCreacion(b) - instanteCreacion(a));
  }, [portafoliosData, rol, user?.rut]);

  // Eliminar bitácora completa (soft delete)
  const deletePortafolio = useCallback(
    async (portafolio) => {
      if (!portafolio?.id || !clienteRut) {
        return { success: false, error: "Datos incompletos" };
      }

      setDeletingPortafolio(true);
      try {
        await eliminarPortafolioMutation({
          id: portafolio.id,
          rut: clienteRut,
          refSucursal: sucursal?.refSucursal,
        }).unwrap();

        return { success: true };
      } catch (error) {
        console.error("Error eliminando bitácora:", error);
        return {
          success: false,
          error: error.data?.message || "Error al eliminar la bitácora",
        };
      } finally {
        setDeletingPortafolio(false);
      }
    },
    [clienteRut, sucursal, eliminarPortafolioMutation],
  );

  useEffect(() => {
    if (clienteRut) {
      createDropboxFolder();
    }
  }, [clienteRut, createDropboxFolder]);

  return {
    portafolios: processedPortafolios,
    loading: isLoading,
    deletingFile,
    deletingPortafolio,
    rol,
    error:
      error?.data?.message || (error ? "Error al cargar portafolios" : null),
    refreshPortafolios: refetch,
    deleteFile,
    deletePortafolio,
  };
};
